import { PointFeature } from "@evergis/sgis/es/features/PointFeature";
import { Poly } from "@evergis/sgis/es/features/Poly";
import polygonClipping, { Geom } from "polygon-clipping";
import { useCallback, useEffect, useRef } from "react";
import { SelectionHistoryFeature } from "../../../../../../ducks/geometrySelection/geometrySelection";
import { useGeometrySelection } from "../../../../../../hooks/geometrySelection/useGeometrySelection";
import { useGeometrySelectionContext } from "../../../../../../hooks/geometrySelection/useGeometrySelectionContext";
import { ShadowedPointSymbol } from "../../../../../../react-sgis/symbols/editorSymbols/ShadowedPointSymbol";
import { ShadowedPolySymbol } from "../../../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";
import { getRingsWithoutDuplicates } from "../../../../../../utils/geometry";
import { GeometrySelectionToolTypes, GeometrySelectionTypes } from "../../types";
import { createPointFeature } from "../utils/createPointFeature";
import { createPointsPolygonFeature } from "../utils/createPointsPolygonFeature";
import { createPolygonFeature } from "../utils/createPolygonFeature";
import { createPolylineFeature } from "../utils/createPolylineFeature";
import { usePolygonEditor } from "./usePolygonEditor";
import { GeometrySelectionSymbolType, useSelectionSymbols } from "./useSelectionSymbols";

export const unionFeaturesRings = (polygons: SelectionHistoryFeature[]) => {
  return polygons.reduce((acc, { _rings }) => [...acc, ..._rings], []);
};

// eslint-disable-next-line max-lines-per-function
export const useSelectionEdit = (geometryType: GeometrySelectionTypes, map, painter, mode, setCreateActive) => {
  const { setFeatures, layer: selectionLayer, editLayer } = useGeometrySelectionContext();
  const [geometrySelection] = useGeometrySelection();
  const editFeature = useRef<PointFeature | Poly>();
  const symbols = useSelectionSymbols();

  // eslint-disable-next-line max-lines-per-function
  const onEditFinish = useCallback(() => {
    let processedFeatures;

    switch (geometryType) {
      case GeometrySelectionTypes.Point:
        processedFeatures = (editFeature.current as Poly).rings.map(contour =>
          createPointFeature(contour[0], map.crs, symbols[GeometrySelectionSymbolType.point] as ShadowedPointSymbol),
        );
        break;
      // polygons need conversion from multiple contours to separate features for polygon-clipping support
      case GeometrySelectionTypes.Polygon:
        processedFeatures = polygonClipping
          .union(
            (editFeature.current as Poly).rings
              .map(contour =>
                createPolygonFeature(
                  [contour],
                  map.crs,
                  symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
                ),
              )
              .map(item => (item as SelectionHistoryFeature)._rings) as Geom,
          )
          // polygonClipping.union adds last point duplicate to polygons without intersection
          .map(getRingsWithoutDuplicates)
          .map(rings =>
            createPolygonFeature(
              rings,
              map.crs,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
            ),
          );
        break;
      case GeometrySelectionTypes.Circle:
        processedFeatures = polygonClipping
          .union(
            (editFeature.current as Poly).rings
              .map(contour =>
                createPolygonFeature(
                  [contour],
                  map.crs,
                  symbols[GeometrySelectionSymbolType.circleCreate] as ShadowedPolySymbol,
                ),
              )
              .map(item => (item as SelectionHistoryFeature)._rings) as Geom,
          )
          // polygonClipping.union adds last point duplicate to polygons without intersection
          .map(getRingsWithoutDuplicates)
          .map(rings =>
            createPolygonFeature(
              rings,
              map.crs,
              symbols[GeometrySelectionSymbolType.circleCreate] as ShadowedPolySymbol,
            ),
          );
        break;
      case GeometrySelectionTypes.Line:
      case GeometrySelectionTypes.Route:
        processedFeatures = (editFeature.current as Poly).rings.map(contour =>
          createPolylineFeature(
            [contour],
            map.crs,
            map.resolution,
            symbols[GeometrySelectionSymbolType.polygonEdit] as ShadowedPolySymbol,
            symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
            symbols[GeometrySelectionSymbolType.polylineEdit] as ShadowedPolySymbol,
            symbols[GeometrySelectionSymbolType.polylineCreate] as ShadowedPolySymbol,
          ),
        );
        break;
      default:
        return;
    }

    setFeatures(processedFeatures);
  }, [setFeatures, geometryType, map.crs, map.resolution]);

  const [editorControl, setEditorActive] = usePolygonEditor(map, painter, editLayer, onEditFinish);

  const getEditFeature = useCallback((): Poly => {
    const { editHistory, history: selectionHistory } = geometrySelection;
    const history = editHistory.step === -1 ? selectionHistory : editHistory;

    switch (geometryType) {
      case GeometrySelectionTypes.Point:
        return createPointsPolygonFeature(
          history.features[history.step].map(({ _position }) => [_position]),
          map.crs,
          symbols[GeometrySelectionSymbolType.pointPolygon] as ShadowedPolySymbol,
        ) as Poly;
      case GeometrySelectionTypes.Polygon:
      case GeometrySelectionTypes.Circle:
        return createPolygonFeature(
          unionFeaturesRings(history.features[history.step]),
          map.crs,
          symbols[GeometrySelectionSymbolType.polygonEdit] as ShadowedPolySymbol,
        ) as Poly;
      case GeometrySelectionTypes.Line:
      case GeometrySelectionTypes.Route:
        return createPolylineFeature(
          unionFeaturesRings(history.features[history.step]),
          map.crs,
          map.resolution,
          symbols[GeometrySelectionSymbolType.polygonEdit] as ShadowedPolySymbol,
          symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
          symbols[GeometrySelectionSymbolType.polylineEdit] as ShadowedPolySymbol,
          symbols[GeometrySelectionSymbolType.polylineCreate] as ShadowedPolySymbol,
          true,
        ) as Poly;
      default:
        return null;
    }
  }, [map.crs, map.resolution, geometryType, geometrySelection]);

  useEffect(() => {
    if (mode === GeometrySelectionToolTypes.Edit) {
      setCreateActive(false);

      editFeature.current = getEditFeature();

      editLayer.features = [editFeature.current];
      editorControl.activeFeature = editFeature.current;
      setEditorActive(true);
      if (selectionLayer) {
        selectionLayer.hide();
      }
    } else {
      if (editorControl) {
        editorControl.activeFeature = null;
      }
      setEditorActive(false);
      if (editLayer && editLayer.features.length > 0) {
        editLayer.features = [];
      }
      setCreateActive(true);
      if (selectionLayer) {
        selectionLayer.show();
      }
    }
  }, [mode, geometrySelection]);
};
